'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { apiClient } from '@/lib/api-client'
import { appendScopeFilterParams } from '@/lib/dashboard-scope-filters'
import { cn } from '@/lib/utils'

// ============================================================================
// Types
// ============================================================================

interface PolicyViolationStats {
  policy_name: string
  policy_id?: string | null
  violation_count: number
  is_fatal: boolean
  affected_calls: number
  violation_rate: number
}

interface PolicyViolationsData {
  total_violations: number
  total_calls_analyzed: number
  violations: PolicyViolationStats[]
}

// ============================================================================
// Props
// ============================================================================

interface PolicyViolationsCardProps {
  startDate: string
  endDate: string
  isLoading?: boolean
  targetEmployeeId?: number
  selectedProduct?: string
  selectedSite?: string
}

// ============================================================================
// Component
// ============================================================================

export function PolicyViolationsCard({
  startDate,
  endDate,
  isLoading: externalLoading = false,
  targetEmployeeId,
  selectedProduct,
  selectedSite,
}: PolicyViolationsCardProps) {
  const [data, setData] = useState<PolicyViolationsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load policy violations data
  useEffect(() => {
    if (startDate && endDate) {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, targetEmployeeId, selectedProduct, selectedSite])

  const loadData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      appendScopeFilterParams(params, selectedProduct, selectedSite)
      const result = await apiClient.get<PolicyViolationsData>(
        `/api/analysis/policy-violations?${params}`
      )
      setData(result)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load policy violations'
      console.error('Failed to load policy violations:', err)
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // Download CSV
  const downloadCSV = () => {
    if (!data || !data.violations.length) return

    // Build CSV content
    const headers = [
      'Policy Name',
      'Violation Count',
      'Affected Calls',
      'Violation Rate (%)',
      'Is Fatal/Critical'
    ]
    
    const rows = data.violations.map(v => [
      `"${v.policy_name}"`,
      v.violation_count.toString(),
      v.affected_calls.toString(),
      v.violation_rate.toFixed(1),
      v.is_fatal ? 'Yes' : 'No'
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    link.setAttribute('href', url)
    link.setAttribute('download', `policy-violations-${startDate}-to-${endDate}.csv`)
    link.style.visibility = 'hidden'
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Loading state
  if (isLoading || externalLoading) {
    return (
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={3} scale={1.1} />
        <CardHeader className='relative z-10'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.clipboardList className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            AI Policy Checklist
          </CardTitle>
        </CardHeader>
        <CardContent className='relative z-10'>
          <div className='space-y-3'>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className='h-16 w-full' />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Card className='relative overflow-hidden border-red-100'>
        <CardWatermark opacity={3} scale={1.1} />
        <CardHeader className='relative z-10'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.clipboardList className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            AI Policy Checklist
          </CardTitle>
        </CardHeader>
        <CardContent className='relative z-10'>
          <div className='flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-700'>
            <Icons.alertCircle className='h-4 w-4' />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  // No data state (no policies configured)
  if (!data || !data.violations.length) {
    return (
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={3} scale={1.1} />
        <CardHeader className='relative z-10'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.clipboardList className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            AI Policy Checklist
          </CardTitle>
        </CardHeader>
        <CardContent className='relative z-10'>
          <motion.div
            className='flex flex-col items-center justify-center py-8 text-center'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 mb-3'>
              <Icons.clipboardList className='h-7 w-7 text-blue-600' />
            </div>
            <h3 className='font-semibold text-brand-navy'>No Policies Configured</h3>
            <p className='mt-1 text-sm text-muted-foreground max-w-xs'>
              Create call-scoring policies to start tracking compliance
            </p>
          </motion.div>
        </CardContent>
      </Card>
    )
  }

  // Main content
  return (
    <Card className='relative overflow-hidden'>
      <CardWatermark opacity={3} scale={1.1} />
      <CardHeader className='relative z-10 pb-3'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='h-10 w-10 rounded-xl bg-gradient-to-br from-brand-cornflower to-brand-purple flex items-center justify-center'>
              <Icons.clipboardList className='h-5 w-5 text-white' strokeWidth={1.5} />
            </div>
            <div>
              <CardTitle className='text-base font-semibold'>AI Policy Checklist</CardTitle>
              <p className='text-xs text-muted-foreground font-normal'>
                {data.total_violations} violations across {data.total_calls_analyzed} calls
              </p>
            </div>
          </div>
          <Button
            onClick={downloadCSV}
            variant='outline'
            size='sm'
            className='gap-2 border-brand-cornflower text-brand-cornflower hover:bg-brand-cornflower hover:text-white'
          >
            <Icons.download className='h-4 w-4' />
            CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className='relative z-10 pt-0'>
        {/* Summary Stats */}
        <div className='mb-4 grid grid-cols-3 gap-3'>
          <div className='rounded-lg bg-brand-cornflower/5 p-3 border border-brand-cornflower/10'>
            <p className='text-xs text-muted-foreground mb-1'>Total Policies</p>
            <p className='text-2xl font-bold text-brand-navy'>{data.violations.length}</p>
          </div>
          <div className='rounded-lg bg-red-50 p-3 border border-red-100'>
            <p className='text-xs text-muted-foreground mb-1'>Total Violations</p>
            <p className='text-2xl font-bold text-red-600'>{data.total_violations}</p>
          </div>
          <div className='rounded-lg bg-emerald-50 p-3 border border-emerald-100'>
            <p className='text-xs text-muted-foreground mb-1'>Perfect Score</p>
            <p className='text-2xl font-bold text-emerald-600'>
              {data.violations.filter(v => v.violation_count === 0).length}
            </p>
          </div>
        </div>

        {/* Policy Violations List */}
        <div className='space-y-2 max-h-[500px] overflow-y-auto'>
          {data.violations.map((violation, index) => {
            const hasViolations = violation.violation_count > 0
            
            return (
              <motion.div
                key={`${violation.policy_name}-${index}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={cn(
                  'group flex items-center justify-between gap-3 p-3 rounded-xl border transition-all',
                  !hasViolations && 'border-emerald-100 bg-emerald-50/50 hover:bg-emerald-50',
                  hasViolations && violation.is_fatal && 'border-red-200 bg-red-50 hover:bg-red-100',
                  hasViolations && !violation.is_fatal && 'border-amber-100 bg-amber-50 hover:bg-amber-100'
                )}
              >
                <div className='flex items-center gap-3 min-w-0 flex-1'>
                  {/* Icon */}
                  <div
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                      !hasViolations && 'bg-emerald-500 text-white',
                      hasViolations && violation.is_fatal && 'bg-red-500 text-white',
                      hasViolations && !violation.is_fatal && 'bg-amber-500 text-white'
                    )}
                  >
                    {!hasViolations ? (
                      <Icons.checkCircle className='h-4 w-4' strokeWidth={2} />
                    ) : violation.is_fatal ? (
                      <Icons.alertCircle className='h-4 w-4' strokeWidth={2} />
                    ) : (
                      <Icons.alertTriangle className='h-4 w-4' strokeWidth={2} />
                    )}
                  </div>

                  {/* Policy Info */}
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2'>
                      <p className='font-medium text-brand-navy truncate text-sm'>
                        {violation.policy_name}
                      </p>
                      {violation.is_fatal && hasViolations && (
                        <span className='px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-600 text-white'>
                          FATAL
                        </span>
                      )}
                      {!hasViolations && (
                        <span className='px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-600 text-white'>
                          PERFECT
                        </span>
                      )}
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      {hasViolations ? (
                        <>
                          {violation.affected_calls} call{violation.affected_calls !== 1 ? 's' : ''} affected
                          {' • '}
                          {violation.violation_rate.toFixed(1)}% violation rate
                        </>
                      ) : (
                        <>No violations in {data.total_calls_analyzed} call{data.total_calls_analyzed !== 1 ? 's' : ''}</>
                      )}
                    </p>
                  </div>
                </div>

                {/* Violation Count Badge */}
                <div
                  className={cn(
                    'flex items-center justify-center rounded-lg px-3 py-1.5 min-w-[50px]',
                    !hasViolations && 'bg-emerald-600 text-white',
                    hasViolations && violation.is_fatal && 'bg-red-600 text-white',
                    hasViolations && !violation.is_fatal && 'bg-amber-600 text-white'
                  )}
                >
                  <span className='text-lg font-bold'>{violation.violation_count}</span>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Footer Info */}
        <div className='mt-4 rounded-lg bg-blue-50 p-3 border border-blue-100'>
          <p className='text-xs text-blue-700 flex items-center gap-2'>
            <Icons.info className='h-3 w-3' />
            Showing all policy violations from {startDate} to {endDate}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
